import React from "react";
import { Box, Tab, Tabs, Typography } from "@mui/material";
import type { NoticeFeedTab } from "../../utils/noticeFeedFilters";

interface NoticeFeedTabsProps {
  activeTab: NoticeFeedTab;
  onTabChange: (tab: NoticeFeedTab) => void;
  scheduledCount: number;
  sentCount: number;
}

const tabSx = {
  textTransform: "none" as const,
  fontWeight: 400,
  fontSize: { xs: "0.9rem", sm: "1rem" },
  minHeight: 44,
  px: { xs: 1.5, sm: 2 },
  "&.Mui-selected": {
    fontWeight: 400,
  },
};

const NoticeFeedTabs: React.FC<NoticeFeedTabsProps> = ({
  activeTab,
  onTabChange,
  scheduledCount,
  sentCount,
}) => {
  const tabIndex = activeTab === "sent" ? 0 : 1;

  return (
    <Box
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        mb: 2.5,
      }}
    >
      <Tabs
        value={tabIndex}
        onChange={(_, value: number) =>
          onTabChange(value === 0 ? "sent" : "scheduled")
        }
        aria-label="Notice feed tabs"
        sx={{
          minHeight: 44,
          "& .MuiTabs-indicator": { height: 2 },
        }}
      >
        <Tab
          label={
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              Sent
              <Typography
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: activeTab === "sent" ? "primary.main" : "text.secondary",
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 1,
                  lineHeight: 1.4,
                  bgcolor:
                    activeTab === "sent"
                      ? (theme) => `${theme.palette.primary.main}18`
                      : "action.hover",
                }}
              >
                {sentCount}
              </Typography>
            </Box>
          }
          sx={tabSx}
          data-testid="notice-feed-tab-sent"
        />
        <Tab
          label={
            <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
              Scheduled
              <Typography
                component="span"
                sx={{
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  color: activeTab === "scheduled" ? "primary.main" : "text.secondary",
                  px: 0.75,
                  py: 0.15,
                  borderRadius: 1,
                  lineHeight: 1.4,
                  bgcolor:
                    activeTab === "scheduled"
                      ? (theme) => `${theme.palette.primary.main}18`
                      : "action.hover",
                }}
              >
                {scheduledCount}
              </Typography>
            </Box>
          }
          sx={tabSx}
          data-testid="notice-feed-tab-scheduled"
        />
      </Tabs>
    </Box>
  );
};

export default NoticeFeedTabs;
